import { ACCEPT_INVITATION, ACCOUNT_CHECKOUT, LOGIN, LOGOUT, PRICING, PROVIDER_AUTH, SIGN_IN, SIGN_OUT, SIGN_UP } from './routes'

export const GoogleDriveScope = 'https://www.googleapis.com/auth/drive'
export const GoogleSpreadsheetScope = 'https://www.googleapis.com/auth/spreadsheets'

export const OAUTH_BASIC_USER_PROFILE_SCOPES = 'openid email profile'
export const oauthScope = (
    'openid ' +
    'https://www.googleapis.com/auth/userinfo.email ' +
    'https://www.googleapis.com/auth/userinfo.profile ' +
    'https://www.googleapis.com/auth/drive ' +
    'https://www.googleapis.com/auth/spreadsheets'
)

export const GIS_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount'
export const GIS_OAUTH_ERRORS = ['access_denied', 'popup_closed', 'popup_failed_to_open']
/**
 * Scopes that are considered essential and the application may not work properly without them.
 */
export const OAUTH_ESSENTIAL_APP_SCOPES = [GoogleDriveScope, GoogleSpreadsheetScope]

export const GIS_IDENTIFIABLE_URL_PARAMS = ['code', 'access_token', 'error', 'magicToken']
export const GIS_COMMON_URL_PARAMS = [
    'code', 'access_token', 'scope', 'error', 'state', 'hd',
    'authuser', 'prompt', 'session_state', 'magicToken'
]
export const NON_REDIRECT_ENTRY_PAGES = ['login', 'sign-in', 'provider-auth', 'sign-out', 'logout']
// SG-12652 s66 — SIGN_OUT/LOGOUT must be considered non-valid entry paths.
// If a user signs out then signs in via OAuth, LoginPage:172 persists the
// CURRENT entryPageUrl (which post-sign-out is /sign-out) to localStorage.
// PrivateRoute then replays that URL after auth → user lands on /sign-out
// → /sign-out Route's render calls handleSignOut → cleanSession wipes IDB
// → PrivateRoute bounces back to /sign-in. The pre-fix gap allowed
// /sign-out (and /logout) through the entry-page redirect because only
// the auth-related paths LOGIN/SIGN_IN/SIGN_UP/PROVIDER_AUTH were guarded.
//
// SG-14557 T674 — same gap, same shape, for ACCEPT_INVITATION. An
// unauthenticated invitee's `entryPageUrl` is `/accept-invitation?token=...`
// (set once at boot); "Continue to sign in" never changes it, so it survives
// the whole OAuth detour untouched. Both `onForceRedirectAfterSignUp`
// (redux/sagas/App) and PrivateRoute's own stored-entry replay read this
// SAME list to decide whether to send the freshly authenticated visitor
// back to their entry page — and unguarded, both did: a brand-new invitee
// who just finished sign-up landed back on the signed-out invitation card
// (or, once PendingTeamInvitationResumer's automatic accept had already
// spent the token, on the "this invitation was already used" terminal
// screen) instead of inside the app. An invitation link is
// exactly the class of screen this list exists for — a waypoint on the way
// IN, never a destination to return TO once that trip is done.
export const NON_VALID_ENTRY_PATHS = [LOGIN, LOGOUT, SIGN_IN, SIGN_OUT, SIGN_UP, PROVIDER_AUTH, ACCEPT_INVITATION]
// SG-14558 — a fresh signup never lands on a sales route. Every web signup
// already holds a 14-day trial, so the post-signup destination is the
// first-workflow setup, never the pricing table. A signed-out visit to /pricing
// (or /account/checkout) bounces to /sign-in, LoginPage persists that URL as the
// entry page, SignUpPage replays it into `entryPageUrl`, and
// onForceRedirectAfterSignUp used to keep it — SG-7789 had whitelisted exactly
// these two "sales routes" so a 2020 buyer was not interrupted. The trial-first
// funnel reverses that for the post-signup redirect only: these paths fall back
// to ROOT (the empty workspace redirects to the get-started page on the 2024 UI;
// the legacy UI still creates and opens the first workflow). Sign-in and
// deep links (templates, shared workflows) are untouched.
export const SIGN_UP_LANDING_EXCLUDED_PATHS = [PRICING, ACCOUNT_CHECKOUT]

/**
 * Whether a normalised entry pathname is a sales route a fresh signup must not land on.
 * Both writers of the post-signup destination (the redirect saga and PrivateRoute's
 * stored-entry replay) consult this so the rule cannot drift between them.
 *
 * @param {string} pathname - The entry pathname, app URL already stripped.
 * @returns {boolean} True when the pathname is (or sits under) a sales route.
 */
export const isSignUpLandingExcludedPath = (pathname) => (
    typeof pathname === 'string' &&
    SIGN_UP_LANDING_EXCLUDED_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`))
)

// SG-14557 — T674 above strips ACCEPT_INVITATION from the PATHNAME so a fresh
// invitee lands in the app, not back on the invitation card, but it never touched
// the query string: entryPageUrl is `/accept-invitation?token=<bearer invitation
// token>`, so that token was riding along into the landed URL (`/?token=...`) —
// browser history, referrers, anything reading the address bar. Scoped to the
// invitation route specifically, not to every NON_VALID_ENTRY_PATHS entry and not
// by dropping `token` globally: the other entries (sign-out, logout) carry no
// credentials, and `token` is a generic-looking name — `grep`, 2026-09-12, found no
// other route in src/ that reads a `token` query param. AcceptInvitationPage is the
// only reader, and it moves the token into Redux/sessionStorage at the "Create my
// account" / "Sign in with another account" click (see PendingTeamInvitationResumer),
// which is the ONLY place anything downstream reads it from — the invitation is
// already fully spent by the time this fires.
export const ACCEPT_INVITATION_URL_PARAMS = ['token']

// SG-14557 P1 — the first fix (isAcceptInvitationEntryPath, checked at the redirect
// saga only) was defeated: NON_VALID_ENTRY_PATHS has SEVEN consumers, and on the
// ordinary signup path SignUpPage's `handleReplaceEntryPage` strips the pathname out
// of `entryPageUrl` and writes the (now path-less) result back to Redux BEFORE the
// saga ever runs. By the time the saga checked "is this the invitation route", the
// pathname was already gone, the check came back false, and the token rode into
// `/?token=...` untouched — a route check at ONE site, downstream of a stripper at
// ANOTHER, can always be defeated by whichever site runs first.
//
// The fix is to make the two operations — strip the pathname, strip the invitation's
// query params — inseparable: one function, keyed off the pathname BEFORE either
// happens, called by every consumer that strips a NON_VALID_ENTRY_PATHS entry out of
// a pathname. There is then no ordering in which the path can be gone and the token
// still there, because there is no step where only one of the two ran.
/**
 * Strip every NON_VALID_ENTRY_PATHS entry out of `pathname` and, in the same step,
 * strip the invitation's bearer query params out of `searchParams` — atomically,
 * keyed off the ORIGINAL (pre-strip) pathname. This must be the only way a consumer
 * strips a NON_VALID_ENTRY_PATHS entry out of a pathname; see the note above for why.
 *
 * Substring matching (`.includes`), not an exact/startsWith check: callers pass this
 * both app-relative pathnames (`/accept-invitation`) and full entry URLs still
 * carrying the domain (`https://app.sheetgo.com/accept-invitation`), and the loose
 * match already used for the NON_VALID_ENTRY_PATHS strip below is what those callers
 * rely on.
 *
 * @param {string} pathname - The entry pathname, not yet stripped.
 * @param {URLSearchParams} [searchParams] - Mutated in place, when supplied, to also
 * drop the invitation's query params when `pathname` is the invitation route.
 * @returns {string} `pathname` with every NON_VALID_ENTRY_PATHS entry removed.
 */
export const stripNonValidEntryPath = (pathname, searchParams) => {
    if (typeof pathname !== 'string' || !NON_VALID_ENTRY_PATHS.some(p => pathname.includes(p))) {
        return pathname
    }
    if (searchParams && pathname.includes(ACCEPT_INVITATION)) {
        ACCEPT_INVITATION_URL_PARAMS.forEach(p => searchParams.delete(p))
    }
    return NON_VALID_ENTRY_PATHS.reduce((stripped, p) => stripped.replace(p, ''), pathname)
}
export const FORCED_REDIRECT_AFTER_SIGN_UP_TIMEOUT = 5000
export const LAST_CLICKED_PROVIDER_STORAGE_KEY = 'LAST_CLICKED_PROVIDER'

export const BigQueryReadOnlyScope = 'https://www.googleapis.com/auth/bigquery.readonly'
export const BigQueryScope = 'https://www.googleapis.com/auth/bigquery'
export const GoogleDocsScope = 'https://www.googleapis.com/auth/documents'
export const GmailScope = 'https://www.googleapis.com/auth/gmail.send'
export const GmailComposeScope = 'https://www.googleapis.com/auth/gmail.compose'
// SG-14515 — Gmail as a source. DELIBERATELY NOT in POSSIBLE_ADDITIONAL_SCOPES or
// oauthFullScope: both are bulk buckets that get requested wholesale on ordinary reconnect
// and full-scope sign-in, which would put a restricted scope in front of every customer.
// See docs/specs/gmail-source-demo-design.md §5.3. Two tests pin this.
export const GmailReadonlyScope = 'https://www.googleapis.com/auth/gmail.readonly'

/**
 * OAuth scope including all available Google services.
 * Used when full_scopes=true query param is present on sign-in.
 */
export const oauthFullScope = [
    oauthScope,
    GoogleDocsScope,
    GmailScope,
    BigQueryReadOnlyScope
].join(' ')

export const POSSIBLE_ADDITIONAL_SCOPES = [
    BigQueryReadOnlyScope,
    GoogleDocsScope,
    BigQueryScope,
    GmailScope
]

export const NonExclusiveScopes = {
    [GmailScope]: [GmailScope, GmailComposeScope]
}

// These users won't be affected by lockable inside the app
// "@sheetgo.com" users are already unaffected
export const NonLockableUsers = [
    'user-sheetgo-210@gedu-demo-sheetgo.com',
    'user-sheetgo-211@gedu-demo-sheetgo.com',
    'user-sheetgo-212@gedu-demo-sheetgo.com',
    'user-sheetgo-213@gedu-demo-sheetgo.com',
    'user-sheetgo-214@gedu-demo-sheetgo.com'
]

// When in the process of OAuth verification request, we need to use the official Google sign-in button
// https://developers.google.com/identity/branding-guidelines
export const useGoogleSignInBranding = false

export const PublicEmailDomains = [
    'gmail',
    'outlook',
    'hotmail',
    'msn',
    'live',
    'passport',
    'yahoo',
    'icloud',
    'mail',
    'inbox',
    'aol',
    'zoho',
    'yandex'
]

export const AuthClientVersion = {
    DEFAULT: 'default',
    ALT: 'alternative'
}

/**
 * Check if a given email is from a public domain.
 *
 * @param {string} userEmail - The user email.
 * @returns {boolean} If it's from public domain or not.
 */
export const hasPublicDomain = (userEmail) => {
    if (typeof userEmail !== 'string' || !userEmail.includes('@')) return true
    const userDomain = `@${userEmail.split('@')[1]}`
    return Boolean(PublicEmailDomains.find(pd => userDomain.includes(`@${pd}.`)))
}

/**
 * Check if a given domain is public.
 *
 * @param {string} company - The user email.
 * @returns {boolean} If it's from public domain or not.
 */
export const isPublicDomain = domain => {
    if (typeof company !== 'string') return true
    return Boolean(PublicEmailDomains.find(pd => domain.includes(`${pd}.`)))
}

/**
 * Get one of the specific colors for the sign up page avatars.
 *
 * @returns {string} A random color.
 */
export const getOauthAvatarColor = () => {
    const colors = [
        '#10A39A', '#F56BF0', '#FF73AE',
        '#FFB054', '#3AB8FF', '#A369FE'
    ]
    const randomIndex = Math.floor(Math.random() * colors.length)
    return colors[randomIndex]
}
